import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

export default function SettingsPage() {
  const { user } = useAuth();
  const { plan, subscribed, subscriptionEnd, analysesUsed, analysesLimit } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);
  const navigate = useNavigate();
  usePageMeta({ title: "Settings — Unranked" });

  // Profile: email
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  // Profile: password
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleUpdateEmail = async () => {
    if (!newEmail.trim()) return;
    setEmailLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail.trim() });
      if (error) throw error;
      toast({ title: "Confirmation sent", description: "Check both your old and new email to confirm the change." });
      setNewEmail("");
    } catch (e: any) {
      toast({ title: "Error", description: e.message || "Could not update email.", variant: "destructive" });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (newPassword.length < 6) {
      toast({ title: "Too short", description: "Password must be at least 6 characters.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Mismatch", description: "Passwords do not match.", variant: "destructive" });
      return;
    }
    setPasswordLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast({ title: "Password updated", description: "Your password has been changed." });
      setNewPassword("");
      setConfirmPassword("");
    } catch (e: any) {
      toast({ title: "Error", description: e.message || "Could not update password.", variant: "destructive" });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch {
      toast({ title: "Error", description: "Could not open subscription management.", variant: "destructive" });
    } finally {
      setPortalLoading(false);
    }
  };

  const planLabel = plan === "growth" ? "Growth" : plan === "starter" ? "Starter" : "Free";

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-display">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your account</p>
      </div>

      {/* Profile */}
      <div className="rounded-lg border border-border bg-card p-8 space-y-6">
        <h2 className="text-lg font-semibold text-foreground">Profile</h2>

        <div className="space-y-1">
          <Label className="text-muted-foreground">Current email</Label>
          <p className="text-foreground">{user?.email}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-email">New email address</Label>
          <div className="flex gap-3 items-center">
            <Input
              id="new-email"
              type="email"
              placeholder="new@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="max-w-sm"
            />
            <Button variant="outline" onClick={handleUpdateEmail} disabled={emailLoading || !newEmail.trim()}>
              {emailLoading ? "Sending…" : "Update Email"}
            </Button>
          </div>
        </div>

        <div className="border-t border-border pt-4 space-y-2">
          <Label htmlFor="new-password">New password</Label>
          <Input
            id="new-password"
            type="password"
            placeholder="Min. 6 characters"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="max-w-sm"
          />
          <Label htmlFor="confirm-password">Confirm password</Label>
          <Input
            id="confirm-password"
            type="password"
            placeholder="Re-enter password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="max-w-sm"
          />
          <Button variant="outline" onClick={handleUpdatePassword} disabled={passwordLoading || !newPassword}>
            {passwordLoading ? "Updating…" : "Update Password"}
          </Button>
        </div>
      </div>

      {/* Manage Plan */}
      <div className="rounded-lg border border-border bg-card p-8 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Manage Plan</h2>

        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-foreground">{planLabel}</span>
          <Badge variant={subscribed ? "default" : "secondary"}>
            {subscribed ? "Active" : "Free"}
          </Badge>
        </div>

        {subscriptionEnd && (
          <div>
            <p className="text-sm text-muted-foreground">Renews on</p>
            <p className="text-foreground">{new Date(subscriptionEnd).toLocaleDateString()}</p>
          </div>
        )}

        {/* Usage */}
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">Analyses this period</p>
          {analysesLimit === null ? (
            <p className="text-foreground font-medium">{analysesUsed} used — Unlimited</p>
          ) : (
            <>
              <p className="text-foreground font-medium">
                {analysesUsed} / {analysesLimit} used
              </p>
              <Progress value={(analysesUsed / analysesLimit) * 100} className="h-2 max-w-xs" />
              <p className="text-xs text-muted-foreground">
                {Math.max(0, analysesLimit - analysesUsed)} remaining
                {plan === "free" ? " (no monthly renewal)" : ""}
              </p>
            </>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          {subscribed ? (
            <Button variant="outline" onClick={handleManageSubscription} disabled={portalLoading}>
              {portalLoading ? "Loading…" : "Manage Subscription"}
            </Button>
          ) : (
            <Button onClick={() => navigate("/pricing")} className="glow-primary">
              Upgrade Plan
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
